import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Radio, Pause, Play, Edit3, Download, ExternalLink,
  ChevronLeft, ChevronRight, Eye, EyeOff, Filter,
  Loader2, AlertCircle, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronUp, Users, BarChart3, Link2, Building2
} from 'lucide-react';
import { useSignalData } from '../hooks/useSignalData';
import { useRBAC } from '../contexts/RBACContext';
import { PERMISSIONS } from '../config/rbac';
import SignalProfileSetup from '../components/signals/SignalProfileSetup';
import SignalReviewQueue from '../components/signals/SignalReviewQueue';
import ProfileListAdmin from '../components/signals/ProfileListAdmin';
import ProfileUserManagement from '../components/signals/ProfileUserManagement';
import IntelligenceOverview from '../components/signals/IntelligenceOverview';
import SourceRegistryAdmin from '../components/signals/SourceRegistryAdmin';
import SignalFilterBar from '../components/signals/SignalFilterBar';
import CompanySpine from '../components/signals/CompanySpine';
import { useAuth } from '../contexts/AuthContext';

const PAGE_SIZE = 10;

const SOURCE_DISPLAY = {
  gunb_permits: { label: 'GUNB', color: 'bg-orange-100 text-orange-700' },
  gunb: { label: 'GUNB', color: 'bg-orange-100 text-orange-700' },
  sudop: { label: 'SUDOP', color: 'bg-purple-100 text-purple-700' },
  ted_tenders: { label: 'TED', color: 'bg-blue-100 text-blue-700' },
  ted_search: { label: 'TED', color: 'bg-blue-100 text-blue-700' },
  biznes_gov_pl: { label: 'Biznes.gov', color: 'bg-green-100 text-green-700' },
  przetargi_info: { label: 'Przetargi', color: 'bg-indigo-100 text-indigo-700' },
  sam_gov: { label: 'SAM.gov', color: 'bg-red-100 text-red-700' },
  hilma_fi: { label: 'Hilma', color: 'bg-sky-100 text-sky-700' },
  stip_fi: { label: 'STIP', color: 'bg-emerald-100 text-emerald-700' },
  google_news: { label: 'Google News', color: 'bg-gray-100 text-gray-700' },
  ezamowienia_bzp: { label: 'BZP', color: 'bg-amber-100 text-amber-700' },
};

const getSourceTags = (signal) => {
  const sources = new Set();
  if (signal.scored_signal_sources) {
    signal.scored_signal_sources.forEach(sss => {
      const src = sss.raw_signals?.source;
      if (src) sources.add(src);
    });
  }
  return [...sources];
};

// Helper component to load full profile data for editing
// Uses RPC to bypass RLS so assigned editors can also load profile data
const EditProfileLoader = ({ profileId, currentUserEmail, onSubmit }) => {
  const [profileData, setProfileData] = React.useState(null);
  const [loadError, setLoadError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const loadProfile = async () => {
      try {
        const { supabase } = await import('../config/supabase');
        // Try direct query first (works for owners and admins with RLS)
        const { data, error } = await supabase
          .from('signal_profiles')
          .select('*')
          .eq('id', profileId)
          .maybeSingle();
        
        if (data && !error) {
          if (!cancelled) setProfileData(data);
          return;
        }

        // Fallback: use RPC for assigned users (bypasses RLS, returns ALL fields)
        const { data: fullProfile, error: rpcErr } = await supabase
          .rpc('get_profile_for_edit', { 
            p_profile_id: profileId, 
            p_user_email: (currentUserEmail || '').toLowerCase() 
          });
        
        if (!rpcErr && fullProfile && !fullProfile.error) {
          if (!cancelled) setProfileData(fullProfile);
          return;
        }

        throw new Error(fullProfile?.error || 'Profile not found or no access');
      } catch (err) {
        console.error('Failed to load profile for editing:', err);
        if (!cancelled) setLoadError(err.message);
      }
    };
    loadProfile();
    return () => { cancelled = true; };
  }, [profileId, currentUserEmail]);

  if (loadError) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-600 text-sm">Failed to load profile: {loadError}</p>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading profile data...</span>
      </div>
    );
  }

  return <SignalProfileSetup onSubmit={onSubmit} existingProfile={profileData} />;
};

const Signals = () => {
  const { isAdminOrHigher, hasPermission } = useRBAC();
  const canManage = hasPermission(PERMISSIONS.MANAGE_SIGNALS);
  const { currentUser } = useAuth();

  // ─── State ───
  const [activeTab, setActiveTab] = useState('new');
  const [adminTab, setAdminTab] = useState('intelligence');
  const [selectedAdminProfile, setSelectedAdminProfile] = useState(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('relevance_score');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showEditForm, setShowEditForm] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false); // true = clean create form, false = edit existing
  const [showProfileList, setShowProfileList] = useState(false); // 🆕 NEW
  const [expandedSignalId, setExpandedSignalId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [showUserManagement, setShowUserManagement] = useState(null); // profile ID for user management modal
  // Signal filters. These apply to BOTH the Review Queue and the All
  // Signals view; previously they were forced to '' outside the queue,
  // which made country / event-type filtering unreachable for end users.
  const [reviewCountryFilter, setReviewCountryFilter] = useState('');
  const [reviewEventTypeFilter, setReviewEventTypeFilter] = useState('');
  const [reviewMinScoreFilter, setReviewMinScoreFilter] = useState(0);
  const [originFilter, setOriginFilter] = useState('');

  // Map tabs to filters
  const statusFilter = activeTab === 'all' ? 'all' : activeTab;
  const reviewFilter = canManage && adminTab === 'review' ? 'pending_review' : 'all';

  const {
    profile,
    allProfiles,
    accessibleProfiles,
    loading,
    error,
    signals,
    signalsLoading,
    total,
    totalPages,
    createProfile,
    updateProfile,
    toggleActive,
    updateSignalStatus,
    reviewSignal,
    batchReviewSignals,
    markDelivered,
    filterOptions,
    fetchAllMatchingIds,
    loadIntelligenceOverview,
    loadSourceRegistry,
    loadLatestReport,
    loadReportHistory,
    updateSourceRegistryEntry,
    loadProfileTargetConfig,
    loadCompanies,
    loadCompanyCorroboration,
    loadGrantSources,
  } = useSignalData({
    statusFilter: reviewFilter !== 'all' ? 'all' : statusFilter,
    reviewFilter,
    sortBy,
    sortOrder,
    page,
    pageSize: PAGE_SIZE,
    adminProfileId: selectedAdminProfile,
    countryFilter: reviewCountryFilter,
    eventTypeFilter: reviewEventTypeFilter,
    minScoreFilter: reviewMinScoreFilter,
    originFilter,
  });

  // Jump from an Intelligence card/tile straight into a filtered list.
  const applySignalFilters = useCallback((next) => {
    if (next.country !== undefined) setReviewCountryFilter(next.country);
    if (next.eventType !== undefined) setReviewEventTypeFilter(next.eventType);
    if (next.minScore !== undefined) setReviewMinScoreFilter(next.minScore);
    if (next.origin !== undefined) setOriginFilter(next.origin);
    setPage(1);
    if (next.goToReview && canManage) {
      // Drill-down from IntelligenceOverview → open Review Queue so user sees pending signals
      setActiveTab('all');
      setAdminTab('review');
    } else if (next.goToSignals) {
      setActiveTab('all');
      setAdminTab('signals');
    }
  }, [canManage]);

  const clearSignalFilters = useCallback(() => {
    setReviewCountryFilter('');
    setReviewEventTypeFilter('');
    setReviewMinScoreFilter(0);
    setOriginFilter('');
    setPage(1);
  }, []);


  // ─── Handlers ───
  const handleCreateProfile = useCallback(async (data) => {
    setActionError(null);
    try {
      await createProfile(data);
      setIsCreatingNew(false);
      setShowEditForm(false);
    } catch (err) {
      setActionError(err.message || 'Failed to create profile');
      throw err; // Re-throw so SignalProfileSetup can show its own error
    }
  }, [createProfile]);

  const handleUpdateProfile = useCallback(async (data) => {
    setActionError(null);
    try {
      await updateProfile({
        org_name: data.org_name,
        org_description: data.org_description,
        org_industry: data.org_industry,
        org_website: data.org_website,
        target_config: data.target_config,
        ...(data.run_interval_days != null && { run_interval_days: data.run_interval_days }),
        ...(data.max_daily_runs != null && { max_daily_runs: data.max_daily_runs }),
      });
      setShowEditForm(false);
      setIsCreatingNew(false);
    } catch (err) {
      setActionError(err.message || 'Failed to update profile');
      throw err;
    }
  }, [updateProfile]);

  const handleToggleActive = useCallback(async () => {
    setActionError(null);
    try {
      await toggleActive();
    } catch (err) {
      setActionError(err.message);
    }
  }, [toggleActive]);

  const handleStatusChange = useCallback(async (signalId, status) => {
    setActionError(null);
    try {
      await updateSignalStatus(signalId, status);
    } catch (err) {
      setActionError(err.message);
    }
  }, [updateSignalStatus]);

  const handleReviewApprove = useCallback(async (signalId) => {
    await reviewSignal(signalId, 'approve');
  }, [reviewSignal]);

  const handleReviewReject = useCallback(async (signalId) => {
    await reviewSignal(signalId, 'reject');
  }, [reviewSignal]);

  // Mark delivered when user first sees a signal
  const handleSignalView = useCallback((signalId) => {
    markDelivered(signalId);
  }, [markDelivered]);

  // Export CSV
  const handleExport = useCallback(() => {
    if (!signals.length) return;
    const headers = ['Company', 'Country', 'Events', 'Score', 'Confidence', 'Summary', 'Why Now', 'Window', 'Status', 'Created'];
    const rows = signals.map(s => [
      s.company,
      s.country || '',
      (s.event_types || []).join('; '),
      s.relevance_score,
      s.confidence,
      (s.summary_en || '').replace(/"/g, '""'),
      (s.why_now_en || '').replace(/"/g, '""'),
      s.purchase_window || '',
      s.status,
      new Date(s.created_at).toLocaleDateString(),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `signals-${profile?.org_name || 'export'}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [signals, profile]);

  // ─── Event type formatting ───
  const getEventTypeEmoji = (type) => {
    const map = {
      funding: '\uD83D\uDCB0',
      new_line: '\uD83C\uDFED',
      expansion: '\uD83D\uDCC8',
      new_plant: '\uD83C\uDFD7\uFE0F',
      acquisition: '\uD83E\uDD1D',
      leadership_change: '\uD83D\uDC64',
    };
    return map[type] || '';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-700 bg-green-50 border-green-200';
    if (score >= 60) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
    return 'text-gray-700 bg-gray-50 border-gray-200';
  };

  // ─── Loading state ───
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-600">Loading Signal Engine...</span>
      </div>
    );
  }

  // ─── No profile → show setup form ───
  if (!profile && !showEditForm && !showProfileList) {
    return (
      <div className="p-6">
        {canManage && (
          <div className="mb-4">
            <button
              onClick={() => setShowProfileList(true)}
              className="flex items-center text-sm text-primary-600 hover:text-primary-700 transition-colors"
            >
              <Users className="w-4 h-4 mr-1" />
              View All Profiles
            </button>
          </div>
        )}
        <SignalProfileSetup onSubmit={handleCreateProfile} isAdmin={canManage} />
      </div>
    );
  }

  // ─── Show profile list (admin only) ───
  if (showProfileList && canManage) {
    return (
      <div className="p-6">
        <div className="mb-4">
          <button
            onClick={() => setShowProfileList(false)}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Signals
          </button>
        </div>
        <ProfileListAdmin
          onSelectProfile={(profileId) => {
            setSelectedAdminProfile(profileId);
            setShowProfileList(false);
          }}
          onCreateNew={() => {
            setSelectedAdminProfile(null);
            setIsCreatingNew(true);
            setShowProfileList(false);
            setShowEditForm(true);
          }}
          onEditProfile={(profileId) => {
            setSelectedAdminProfile(profileId);
            setIsCreatingNew(false);
            setShowProfileList(false);
            setShowEditForm(true);
          }}
          onManageUsers={(profileId) => {
            setShowUserManagement(profileId);
          }}
          isCmsAdmin={isAdminOrHigher()}
        />
        {/* User Management Modal */}
        {showUserManagement && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Manage Profile Users</h3>
                <button
                  onClick={() => setShowUserManagement(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              <ProfileUserManagement
                profileId={showUserManagement}
                currentUserEmail={currentUser?.email}
                onClose={() => setShowUserManagement(null)}
                isCmsAdmin={isAdminOrHigher()}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Edit/Create form ───
  if (showEditForm) {
    // isCreatingNew=true → always show clean create form
    // isCreatingNew=false → edit mode, find the target profile
    const editTarget = isCreatingNew
      ? null
      : selectedAdminProfile
        ? allProfiles.find(p => p.id === selectedAdminProfile) || profile
        : profile;
    return (
      <div className="p-6">
        <div className="mb-4">
          <button
            onClick={() => { setShowEditForm(false); setIsCreatingNew(false); }}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </button>
        </div>
        {editTarget ? (
          <EditProfileLoader
            profileId={editTarget.id}
            currentUserEmail={currentUser?.email}
            onSubmit={handleUpdateProfile}
          />
        ) : (
          <SignalProfileSetup
            onSubmit={handleCreateProfile}
            existingProfile={null}
            isAdmin={canManage}
          />
        )}
      </div>
    );
  }

  // ─── Get active profile for display ───
  const displayProfile = canManage && selectedAdminProfile
    ? allProfiles.find(p => p.id === selectedAdminProfile) || profile
    : profile;

  // ─── Profile switching ───
  // Admins pick from every profile; everyone else picks from the profiles they
  // have been granted. A user with two assigned profiles used to see only the
  // first with no way to reach the second.
  const switchableProfiles = canManage ? allProfiles : accessibleProfiles;
  const showProfileSwitcher = canManage || accessibleProfiles.length > 1;

  // ─── Main signal list view ───
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-4 sm:p-6 max-w-7xl mx-auto"
    >
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-5 gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-primary-100 rounded-xl flex-shrink-0">
              <Radio className="w-5 h-5 text-primary-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">
                {displayProfile?.org_name || 'Loading...'}
              </h1>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                  displayProfile?.is_active ? 'text-green-700' : 'text-gray-500'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${displayProfile?.is_active ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                  {displayProfile?.is_active ? 'Active' : 'Paused'}
                </span>
                {displayProfile?.auto_approve && (
                  <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
                    Auto-approve
                  </span>
                )}
                {displayProfile?.org_industry && (
                  <span className="text-xs text-gray-500">{displayProfile.org_industry}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          {/* View All Profiles (admin only) */}
          {canManage && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowProfileList(true)}
              className="px-4 py-2 bg-primary-50 text-primary-700 hover:bg-primary-100 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <Users className="w-4 h-4" />
              All Profiles
            </motion.button>
          )}

          {/* Pause/Resume - only for CRM admin, profile owner, or users with edit permission */}
          {(canManage || profile?.is_primary_owner || profile?.can_edit_profile) && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleToggleActive}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                displayProfile?.is_active
                  ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                  : 'bg-green-50 text-green-700 hover:bg-green-100'
              }`}
            >
              {displayProfile?.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {displayProfile?.is_active ? 'Pause' : 'Resume'}
            </motion.button>
          )}

          {/* Edit Profile - only for CRM admin, profile owner, or users with edit permission */}
          {(canManage || profile?.is_primary_owner || profile?.can_edit_profile) && (
            <button
              onClick={() => { setIsCreatingNew(false); setShowEditForm(true); }}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 flex items-center gap-2 transition-colors"
            >
              <Edit3 className="w-4 h-4" />
              Edit Profile
            </button>
          )}

          {/* Manage Users - only for CRM admin or profile owner */}
          {(canManage || profile?.is_primary_owner) && profile?.id && (
            <button
              onClick={() => setShowUserManagement(profile.id)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 flex items-center gap-2 transition-colors"
            >
              <Users className="w-4 h-4" />
              Manage Users
            </button>
          )}

          <button
            onClick={handleExport}
            disabled={!signals.length}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Error banner */}
      {(error || actionError) && (
        <div className="flex items-center p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          {error || actionError}
        </div>
      )}

      {/* Profile switcher + main tabs (visible to all) */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
        {/* Profile switcher — admins see all profiles, others see their own set */}
        {showProfileSwitcher && (
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <select
              value={selectedAdminProfile || ''}
              onChange={(e) => {
                setSelectedAdminProfile(e.target.value || null);
                setPage(1);
              }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              aria-label="Active signal profile"
            >
              <option value="">{canManage ? 'My Profile' : 'Default Profile'}</option>
              {switchableProfiles.map(p => (
                <option key={p.id} value={p.id}>
                  {p.org_name}{p.user_email ? ` (${p.user_email})` : p.user_role ? ` (${p.user_role})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Main tabs — intelligence + signals + review + registry */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto">
          <button
            onClick={() => { setAdminTab('intelligence'); setOriginFilter(''); setPage(1); }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              adminTab === 'intelligence' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Intelligence
          </button>
          <button
            onClick={() => { setAdminTab('signals'); setOriginFilter(''); setPage(1); }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              adminTab === 'signals' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            All Signals
          </button>
          {canManage && (
            <button
              onClick={() => { setAdminTab('review'); setOriginFilter(''); setPage(1); }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                adminTab === 'review' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Review Queue
              {signals.filter(s => s.review_status === 'pending_review').length > 0 && adminTab !== 'review' && (
                <span className="ml-1.5 bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full text-xs">
                  {signals.filter(s => s.review_status === 'pending_review').length}
                </span>
              )}
            </button>
          )}
          {canManage && (
            <button
              onClick={() => { setAdminTab('registry'); setOriginFilter(''); setPage(1); }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                adminTab === 'registry' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Source Registry
            </button>
          )}
          <button
            onClick={() => { setAdminTab('companies'); setOriginFilter(''); setPage(1); }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              adminTab === 'companies' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5" />
              Companies
            </span>
          </button>
        </div>
      </div>

      {/* Intelligence Overview tab */}
      {adminTab === 'intelligence' ? (
        <IntelligenceOverview
          profileId={displayProfile?.id}
          profileName={displayProfile?.org_name}
          targetConfig={displayProfile?.target_config}
          loadIntelligenceOverview={loadIntelligenceOverview}
          loadSourceRegistry={loadSourceRegistry}
          loadLatestReport={loadLatestReport}
          loadReportHistory={loadReportHistory}
          loadProfileTargetConfig={loadProfileTargetConfig}
          onDrillDown={applySignalFilters}
        />
      ) : adminTab === 'registry' && canManage ? (
        <SourceRegistryAdmin
          loadSourceRegistry={loadSourceRegistry}
          updateSourceRegistryEntry={updateSourceRegistryEntry}
        />
      ) : adminTab === 'companies' ? (
        <CompanySpine
          profileId={displayProfile?.id}
          loadCompanies={loadCompanies}
          loadCompanyCorroboration={loadCompanyCorroboration}
          loadGrantSources={loadGrantSources}
        />
      ) : canManage && adminTab === 'review' ? (
        <div>
          <SignalReviewQueue
            signals={signals}
            loading={signalsLoading}
            onApprove={handleReviewApprove}
            onReject={handleReviewReject}
            onBatchApprove={async (ids) => { await batchReviewSignals(ids, 'approve'); }}
            onBatchReject={async (ids) => { await batchReviewSignals(ids, 'reject'); }}
            profileStats={displayProfile}
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            filterOptions={filterOptions}
            countryFilter={reviewCountryFilter}
            eventTypeFilter={reviewEventTypeFilter}
            minScoreFilter={reviewMinScoreFilter}
            originFilter={originFilter}
            onCountryFilterChange={(v) => { setReviewCountryFilter(v); setPage(1); }}
            onEventTypeFilterChange={(v) => { setReviewEventTypeFilter(v); setPage(1); }}
            onMinScoreFilterChange={(v) => { setReviewMinScoreFilter(v); setPage(1); }}
            onOriginFilterChange={(v) => { setOriginFilter(v); setPage(1); }}
            onClearFilters={() => { setReviewCountryFilter(''); setReviewEventTypeFilter(''); setReviewMinScoreFilter(0); setOriginFilter(''); setPage(1); }}
            fetchAllMatchingIds={fetchAllMatchingIds}
          />
        </div>
      ) : (
        <>
          {/* Status tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
            {[
              { key: 'new', label: 'New' },
              { key: 'reviewed', label: 'Reviewed' },
              { key: 'dismissed', label: 'Dismissed' },
              { key: 'all', label: 'All' },
            ].map(tab => {
              const count = tab.key === 'all' ? total : signals.filter(s => s.status === tab.key).length;
              return (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setPage(1); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                  {tab.key !== 'all' && count > 0 && (
                    <span className="ml-1.5 text-xs opacity-60">({count})</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Sort controls */}
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            >
              <option value="relevance_score">Score</option>
              <option value="created_at">Date</option>
              <option value="company">Company</option>
              <option value="confidence">Confidence</option>
              <option value="source_count">Sources</option>
            </select>
            <button
              onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')}
              className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title={sortOrder === 'desc' ? 'Descending' : 'Ascending'}
            >
              {sortOrder === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>

          {/* Country + category filters (previously Review-Queue only) */}
          <SignalFilterBar
            filterOptions={filterOptions}
            countryFilter={reviewCountryFilter}
            eventTypeFilter={reviewEventTypeFilter}
            minScoreFilter={reviewMinScoreFilter}
            originFilter={originFilter}
            onCountryChange={(v) => { setReviewCountryFilter(v); setPage(1); }}
            onEventTypeChange={(v) => { setReviewEventTypeFilter(v); setPage(1); }}
            onMinScoreChange={(v) => { setReviewMinScoreFilter(v); setPage(1); }}
            onOriginChange={(v) => { setOriginFilter(v); setPage(1); }}
            onClear={clearSignalFilters}
          />

          {/* Signal list */}
          {signalsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              <span className="ml-2 text-gray-600 text-sm">Loading signals...</span>
            </div>
          ) : signals.length === 0 ? (
            <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
              <Radio className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-900 mb-1">No signals yet</h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                {profile?.is_active
                  ? 'Monitoring is active. Signals will appear here as the system discovers relevant news.'
                  : 'Monitoring is paused. Resume it to start receiving signals.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {signals.map((signal) => {
                const isExpanded = expandedSignalId === signal.id;

                return (
                  <motion.div
                    key={signal.id}
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-primary-200 hover:shadow-sm transition-all"
                    onMouseEnter={() => {
                      if (!signal.delivered_at) handleSignalView(signal.id);
                    }}
                  >
                    <div className="flex items-start p-3 sm:p-4 gap-3">
                      {/* Score badge */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 border ${getScoreColor(signal.relevance_score)}`}>
                        {signal.relevance_score}
                      </div>

                      {/* Main content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <h4 className="text-sm font-semibold text-gray-900 truncate">{signal.company}</h4>
                          {signal.country && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{signal.country}</span>
                          )}
                          {signal.origin === 'country_pack' && (
                            <span className="text-[10px] bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 rounded font-medium">CP</span>
                          )}
                          {signal.origin === 'google_news' && (
                            <span className="text-[10px] bg-gray-50 text-gray-500 border border-gray-200 px-1.5 py-0.5 rounded font-medium">RSS</span>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1 flex-wrap">
                          {(signal.event_types || []).map(et => (
                            <span key={et} className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                              {getEventTypeEmoji(et)} {et.replace('_', ' ')}
                            </span>
                          ))}
                          {getSourceTags(signal).map(src => {
                            const display = SOURCE_DISPLAY[src] || { label: src.replace(/_/g, ' '), color: 'bg-gray-100 text-gray-600' };
                            return (
                              <span key={src} className={`px-1.5 py-0.5 rounded font-medium ${display.color}`}>
                                {display.label}
                              </span>
                            );
                          })}
                          {signal.source_count > 1 && (
                            <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                              {signal.source_count} sources
                            </span>
                          )}
                          {signal.purchase_window && (
                            <span className="flex items-center gap-1 text-gray-400">
                              <Clock className="w-3 h-3" /> {signal.purchase_window}
                            </span>
                          )}
                        </div>

                        {signal.summary_en && (
                          <p className={`text-xs sm:text-sm text-gray-600 leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
                            {signal.summary_en}
                          </p>
                        )}
                      </div>

                      {/* Actions column */}
                      <div className="flex items-center gap-1.5 ml-4 flex-shrink-0">
                        <button
                          onClick={() => setExpandedSignalId(isExpanded ? null : signal.id)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>

                        {signal.status === 'new' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(signal.id, 'reviewed')}
                              className="px-2.5 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                              title="Mark as reviewed"
                            >
                              <Eye className="w-3.5 h-3.5" /> Reviewed
                            </button>
                            <button
                              onClick={() => handleStatusChange(signal.id, 'dismissed')}
                              className="px-2.5 py-1.5 bg-gray-50 text-gray-600 hover:bg-gray-100 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                              title="Dismiss this signal"
                            >
                              <EyeOff className="w-3.5 h-3.5" /> Dismiss
                            </button>
                          </>
                        )}
                        {signal.status === 'dismissed' && (
                          <button
                            onClick={() => handleStatusChange(signal.id, 'new')}
                            className="px-2.5 py-1.5 bg-gray-50 text-gray-600 hover:bg-gray-100 rounded-lg text-xs font-medium transition-colors"
                          >
                            Restore
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Expanded: details + sources with clickable URLs */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                        {/* Why Now */}
                        {signal.why_now_en && (
                          <div className="mb-3">
                            <span className="text-xs font-medium text-gray-500 block mb-1">Why Now</span>
                            <p className="text-sm text-primary-700 italic">{signal.why_now_en}</p>
                          </div>
                        )}
                        
                        {/* Details grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                          {signal.purchase_window && (
                            <div>
                              <span className="text-xs font-medium text-gray-500">Purchase Window</span>
                              <p className="text-sm text-gray-800">{signal.purchase_window}</p>
                            </div>
                          )}
                          {signal.size_class && (
                            <div>
                              <span className="text-xs font-medium text-gray-500">Deal Size</span>
                              <p className="text-sm text-gray-800 capitalize">{signal.size_class}</p>
                            </div>
                          )}
                          <div>
                            <span className="text-xs font-medium text-gray-500">Confidence</span>
                            <p className="text-sm text-gray-800">{(signal.confidence * 100).toFixed(0)}%</p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500">Score</span>
                            <p className="text-sm text-gray-800">{signal.relevance_score}/100</p>
                          </div>
                        </div>

                        {/* Sources: event type + URL side by side */}
                        {((signal.scored_signal_sources && signal.scored_signal_sources.length > 0) || 
                          (signal.source_urls && signal.source_urls.length > 0)) && (
                          <div className="mb-3">
                            <span className="text-xs font-medium text-gray-500 mb-2 block flex items-center gap-1">
                              <Link2 className="w-3 h-3" />
                              Sources ({signal.source_urls?.length || signal.scored_signal_sources?.length || 0})
                            </span>
                            <div className="space-y-1.5">
                              {(signal.source_urls || []).map((url, idx) => {
                                const matchingSrc = signal.scored_signal_sources?.[idx];
                                return (
                                  <div key={idx} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                                    {matchingSrc && (
                                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium flex-shrink-0">
                                        {matchingSrc.event_type}
                                      </span>
                                    )}
                                    {matchingSrc?.contribution_note && (
                                      <span className="text-xs text-gray-500 flex-shrink-0">{matchingSrc.contribution_note}</span>
                                    )}
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 hover:underline truncate min-w-0 flex-1"
                                    >
                                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                      <span className="truncate">{url.replace(/^https?:\/\/(www\.)?/, '').split('?')[0].substring(0, 60)}</span>
                                    </a>
                                  </div>
                                );
                              })}
                              {/* Show any remaining scored_signal_sources without URLs */}
                              {signal.scored_signal_sources && signal.source_urls && 
                                signal.scored_signal_sources.slice(signal.source_urls.length).map(src => (
                                  <div key={src.id} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium flex-shrink-0">
                                      {src.event_type}
                                    </span>
                                    {src.contribution_note && (
                                      <span className="text-xs text-gray-500">{src.contribution_note}</span>
                                    )}
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )}

                        {/* Contact Information */}
                        {signal.contact_info && (() => {
                          try {
                            const contact = typeof signal.contact_info === 'string' 
                              ? JSON.parse(signal.contact_info) 
                              : signal.contact_info;
                            if (!contact || (!contact.company_name && !contact.website && !contact.location && !contact.key_person)) return null;
                            return (
                              <div className="mb-3">
                                <span className="text-xs font-medium text-gray-500 mb-1.5 block flex items-center gap-1">
                                  Contact Information
                                </span>
                                <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-1.5">
                                  {contact.company_name && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="text-gray-500 text-xs w-20">Company:</span>
                                      <span className="text-gray-800 font-medium">{contact.company_name}</span>
                                    </div>
                                  )}
                                  {contact.website && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="text-gray-500 text-xs w-20">Website:</span>
                                      <a href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`} 
                                         target="_blank" rel="noopener noreferrer"
                                         className="text-primary-600 hover:text-primary-800 hover:underline">{contact.website}</a>
                                    </div>
                                  )}
                                  {contact.location && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="text-gray-500 text-xs w-20">Location:</span>
                                      <span className="text-gray-800">{contact.location}</span>
                                    </div>
                                  )}
                                  {contact.key_person && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="text-gray-500 text-xs w-20">Contact:</span>
                                      <span className="text-gray-800">{contact.key_person}</span>
                                    </div>
                                  )}
                                  {contact.email_domain && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="text-gray-500 text-xs w-20">Email:</span>
                                      <span className="text-gray-700">{contact.email_domain}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          } catch { return null; }
                        })()}
                        
                        <div className="text-xs text-gray-400 pt-2 border-t border-gray-200">
                          Created: {new Date(signal.created_at).toLocaleString()}
                          {signal.reviewed_by && (
                            <span className="ml-3">Reviewed by: {signal.reviewed_by}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <span className="text-sm text-gray-600">
                Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium text-gray-700 px-3">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* User Management Modal (accessible from main view for profile owners) */}
      {showUserManagement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Manage Profile Users</h3>
              <button
                onClick={() => setShowUserManagement(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <ProfileUserManagement
              profileId={showUserManagement}
              currentUserEmail={currentUser?.email}
              onClose={() => setShowUserManagement(null)}
              isCmsAdmin={isAdminOrHigher()}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Signals;
